(() => {
  if (window.swfAUFilters) return;
  window.swfAUFilters = true;
  const instances = new Map();

  function connect(root) {
    if (instances.has(root)) return;
    const controller = new AbortController();
    const { signal } = controller;
    const on = (element, type, handler, options = {}) => element?.addEventListener(type, handler, { ...options, signal });
    const panel = root.querySelector('[data-au-panel]');
    const toggle = root.querySelector('[data-au-toggle]');
    const drawer = root.querySelector('#collection-filter-drawer');
    const drawerToggle = root.querySelector('[data-action="open-drawer"]');
    const sort = root.querySelector('[data-au-sort]');
    const sortToggle = root.querySelector('[data-au-sort-toggle]');
    let submitted = false;
    let fetching = false;
    let loaded = false;
    const isOpen = element => element?.getAttribute('aria-hidden') === 'false';
    const setOpen = (element, button, open) => {
      element?.setAttribute('aria-hidden', String(!open));
      button?.setAttribute('aria-expanded', String(open));
    };

    function updateRange(range, source) {
      const low = range.querySelector('[data-au-slider="min"]');
      const high = range.querySelector('[data-au-slider="max"]');
      const lowNumber = range.querySelector('[data-au-number="min"]');
      const highNumber = range.querySelector('[data-au-number="max"]');
      const maximum = Number(high.max);
      if (source) {
        const isLow = source === low || source === lowNumber;
        const input = isLow ? lowNumber : highNumber;
        const fallback = isLow ? 0 : maximum;
        let value = source.value === '' ? fallback : Number(source.value);
        value = Number.isFinite(value) ? Math.max(0, Math.min(maximum, value)) : fallback;
        value = isLow ? Math.floor(value) : Math.ceil(value);
        value = isLow ? Math.min(value, Number(high.value)) : Math.max(value, Number(low.value));
        (isLow ? low : high).value = String(value);
        input.value = source === input && source.value === '' ? '' : String(value);
      } else {
        if (lowNumber.value !== '') lowNumber.value = String(Math.floor(Number(lowNumber.value)));
        if (highNumber.value !== '') highNumber.value = String(Math.ceil(Number(highNumber.value)));
        low.value = lowNumber.value || '0';
        high.value = highNumber.value || String(maximum);
      }
      range.style.setProperty('--au-low', `${Number(low.value) / (maximum || 1) * 100}%`);
      range.style.setProperty('--au-high', `${Number(high.value) / (maximum || 1) * 100}%`);
      low.style.zIndex = Number(low.value) === maximum ? '2' : '1';
      high.style.zIndex = Number(low.value) === maximum ? '1' : '2';
      const currency = range.dataset.currency || 'AUD';
      const format = new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
        style: 'currency', currency,
        currencyDisplay: currency === 'AUD' ? 'narrowSymbol' : 'symbol',
        minimumFractionDigits: 0, maximumFractionDigits: 0
      });
      range.querySelector('[data-au-amount="min"]').textContent = format.format(low.value);
      range.querySelector('[data-au-amount="max"]').textContent = format.format(high.value);
    }
    const repaint = container => container?.querySelectorAll('[data-au-range]').forEach(range => updateRange(range));
    function reset(container) {
      if (submitted) return;
      container?.querySelector('form')?.reset();
      repaint(container);
    }
    const key = input => JSON.stringify([input.name, input.type, input.type === 'checkbox' ? input.value : '']);

    function restoreOptions(baseline) {
      root.querySelectorAll('[data-au-form]').forEach(form => {
        const current = form.querySelector('.AUFilters__Grid');
        const replacement = baseline.querySelector('.AUFilters__Grid').cloneNode(true);
        const states = new Map(Array.from(current.querySelectorAll('input[name]'), input => [key(input), {
          value: input.value, defaultValue: input.defaultValue, checked: input.checked, defaultChecked: input.defaultChecked
        }]));
        const focused = current.contains(document.activeElement) ? key(document.activeElement) : null;
        const scroll = form.querySelector('.AUFilters__Main');
        const scrollTop = scroll.scrollTop;
        current.querySelectorAll('[data-au-group]').forEach(group => {
          let target = replacement.querySelector(`[data-au-group="${group.dataset.auGroup}"]`);
          group.querySelectorAll('input[type="checkbox"]').forEach(input => {
            if (!input.checked && !input.defaultChecked) return;
            if (!target) {
              target = group.cloneNode(true);
              replacement.append(target);
            } else if (!Array.from(target.querySelectorAll('input')).some(other => key(other) === key(input))) {
              target.querySelector('ul').append(input.closest('li').cloneNode(true));
            }
          });
        });
        const ids = new Map();
        replacement.querySelectorAll('[id]').forEach((element, index) => {
          ids.set(element.id, `${form.dataset.prefix}-restored-${index}`);
          element.id = ids.get(element.id);
        });
        replacement.querySelectorAll('label[for]').forEach(label => { label.htmlFor = ids.get(label.htmlFor) || label.htmlFor; });
        replacement.querySelectorAll('input[name]').forEach(input => {
          const state = states.get(key(input));
          if (!state) return;
          if (input.type === 'checkbox') {
            input.defaultChecked = state.defaultChecked;
            input.checked = state.checked;
          } else {
            input.defaultValue = state.defaultValue;
            input.value = state.value;
          }
        });
        current.replaceWith(replacement);
        repaint(form);
        if (focused) Array.from(replacement.querySelectorAll('input')).find(input => key(input) === focused)?.focus({ preventScroll: true });
        scroll.scrollTop = scrollTop;
      });
    }
    async function loadOptions() {
      if (!Number(root.dataset.activeCount) || loaded || fetching || signal.aborted) return;
      fetching = true;
      root.dataset.optionsState = 'loading';
      try {
        const url = new URL(root.dataset.auOptionsUrl, location.origin);
        if (url.origin !== location.origin) throw new Error('Invalid filter options origin');
        const params = new URLSearchParams(location.search);
        ['preview_theme_id', 'currency', 'country', 'locale'].forEach(name => {
          if (params.has(name)) url.searchParams.set(name, params.get(name));
        });
        const response = await fetch(url, { signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Filter options request failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (signal.aborted || submitted) return;
        const baseline = html.querySelector('[data-au-collection-options] [data-au-form]');
        if (!baseline?.querySelector('.AUFilters__Grid')) throw new Error('Filter options template unavailable');
        restoreOptions(baseline);
        loaded = true;
        root.dataset.optionsState = 'ready';
      } catch (error) {
        if (!signal.aborted) {
          root.dataset.optionsState = 'error';
          console.warn('AU collection-wide choices unavailable; current filters remain usable.', error);
        }
      } finally { fetching = false; }
    }
    function closePanel(focus = false) {
      if (!isOpen(panel)) return;
      setOpen(panel, toggle, false);
      reset(panel);
      if (focus) toggle.focus({ preventScroll: true });
    }
    function closeSort(focus = false) {
      if (!isOpen(sort)) return;
      setOpen(sort, sortToggle, false);
      if (focus) sortToggle.focus({ preventScroll: true });
    }
    on(toggle, 'click', () => {
      if (isOpen(panel)) return closePanel();
      closeSort();
      setOpen(panel, toggle, true);
      loadOptions();
    });
    on(sortToggle, 'click', () => {
      if (isOpen(sort)) return closeSort(true);
      closePanel();
      const bounds = sortToggle.getBoundingClientRect();
      const below = innerHeight - bounds.bottom - 24;
      const above = bounds.top - 24;
      const up = below < 180 && above > below;
      sort.style.setProperty('--au-sort-height', `${Math.max(60, up ? above : below)}px`);
      sort.style.left = `${Math.max(16, Math.min(bounds.right - sort.offsetWidth, innerWidth - sort.offsetWidth - 16))}px`;
      sort.style.top = `${up ? Math.max(16, bounds.top - sort.offsetHeight - 8) : bounds.bottom + 8}px`;
      setOpen(sort, sortToggle, true);
      (sort.querySelector('.is-selected') || sort.querySelector('button')).focus({ preventScroll: true });
    });
    on(document, 'click', event => {
      if (!root.contains(event.target)) closePanel();
      if (!sort?.contains(event.target) && !sortToggle?.contains(event.target)) closeSort();
    });
    on(document, 'focusin', event => {
      if (!sort?.contains(event.target) && !sortToggle?.contains(event.target)) closeSort();
    });
    on(document, 'keydown', event => {
      if (event.key !== 'Escape') return;
      if (isOpen(sort)) { event.preventDefault(); closeSort(true); }
      else if (isOpen(panel)) { event.preventDefault(); closePanel(true); }
    });
    on(document, 'scroll', event => {
      if (!sort?.contains(event.target)) closeSort(sort?.contains(document.activeElement));
    }, { capture: true, passive: true });
    on(window, 'resize', () => {
      closeSort(sort?.contains(document.activeElement));
      if (innerWidth < 1008) closePanel();
    });
    on(root, 'input', event => {
      if (event.target.matches('[data-au-slider]')) updateRange(event.target.closest('[data-au-range]'), event.target);
    });
    on(root, 'change', event => {
      if (event.target.matches('[data-au-number]')) updateRange(event.target.closest('[data-au-range]'), event.target);
    });
    root.querySelectorAll('[data-au-form]').forEach(form => {
      on(form, 'submit', () => { submitted = true; });
      on(form, 'formdata', event => {
        form.querySelectorAll('[data-au-number]').forEach(input => { if (!input.value) event.formData.delete(input.name); });
      });
    });
    on(sort?.querySelector('form'), 'submit', () => { submitted = true; closeSort(); });
    on(window, 'pageshow', () => { submitted = false; });
    const observer = new MutationObserver(() => {
      const open = isOpen(drawer);
      drawerToggle?.setAttribute('aria-expanded', String(open));
      if (open) { closeSort(); loadOptions(); }
      else { reset(drawer); drawerToggle?.focus({ preventScroll: true }); }
    });
    if (drawer) observer.observe(drawer, { attributes: true, attributeFilter: ['aria-hidden'] });
    instances.set(root, () => { controller.abort(); observer.disconnect(); instances.delete(root); });
    repaint(root);
    loadOptions();
  }
  const initialise = container => container.querySelectorAll('[data-au-filters]').forEach(connect);
  initialise(document);
  document.addEventListener('shopify:section:load', event => initialise(event.target));
  document.addEventListener('shopify:section:unload', event => instances.forEach((cleanup, root) => {
    if (event.target.contains(root)) cleanup();
  }));
})();
